import React, { useState, useEffect } from 'react';
import { browserHistory } from 'react-router';
import assertions from 'lib/assertions';
import { consume } from 'lib/queryParams';
import ToastActions from 'actions/ToastActions';
import Button from 'components/ButtonDeprecated';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Select from 'components/Select';
import Textarea from 'components/Textarea';
import CoachingConstants from 'coaching/CoachingConstants';
import { postZendeskRequest, postCoachZendeskRequest } from 'coaching/lib/zendesk';
import {
  GetCoachingHelpForm_me,
  GetCoachingHelpForm_coaching_getCoachingHomeDashboard_participants,
} from 'coaching/queries/__generated__/GetCoachingHelpForm';

export type OnZendeskSubmitSuccess = (email: string) => void;

type FormProps = {
  requester: GetCoachingHelpForm_me | null;
  students?: GetCoachingHelpForm_coaching_getCoachingHomeDashboard_participants[];
  onSubmitSuccess: OnZendeskSubmitSuccess;
  isCoachForm?: boolean;
};

export default function CoachingHelpForm(props: FormProps) {
  const [submitting, setSubmitting] = useState(false);

  const [contactReason, setContactReason] = useState<string | undefined>(undefined);
  const [enteredEmail, setEnteredEmail] = useState<string | undefined>(undefined);
  const [requesterName, setRequesterName] = useState<string | undefined>(undefined);
  const [studentName, setStudentName] = useState<string | undefined>(undefined);
  const [requestDetails, setRequestDetails] = useState<string | undefined>(undefined);

  const { students, requester, isCoachForm } = props;

  const studentNames =
    students?.map(student => {
      const name = student.participantMetadata.studentName;

      return { label: name, value: name };
    }) ?? [];
  const hasStudents = studentNames.length > 0;

  const accountEmail = requester?.email;
  const requestName =
    requesterName ||
    [requester?.first_name, requester?.last_name].join(' ').trim() ||
    accountEmail ||
    enteredEmail ||
    'Anonymous user';

  // Handle query param for the request reason
  useEffect(() => {
    const reason = consume(browserHistory, ['request'])?.request;

    if (typeof reason !== 'string') {
      return;
    }

    const formOption = CoachingConstants.HELP_CONTACT_REASONS.find(
      option => option.value === reason
    );

    if (!formOption) {
      return;
    }

    setContactReason(`${formOption.value}`);
  }, []);

  // Always default to the first participant
  useEffect(() => {
    if (!hasStudents) {
      return;
    }

    setStudentName(studentNames[0].value);
  }, [students]);

  const isSubmitDisabled = (): boolean => {
    const haveName = requester || requesterName;
    const fieldsFilled = isCoachForm
      ? contactReason && requestDetails && (accountEmail || enteredEmail) && haveName
      : contactReason &&
        studentName &&
        requestDetails &&
        (accountEmail || enteredEmail) &&
        haveName;

    if (submitting || !fieldsFilled) {
      return true;
    }

    return false;
  };

  const handleSubmit = () => {
    setSubmitting(true);

    const contactReasons = isCoachForm
      ? CoachingConstants.COACH_HELP_CONTACT_REASONS
      : CoachingConstants.HELP_CONTACT_REASONS;
    const requestReasonLabel =
      contactReasons.find(option => option.value === contactReason)?.label ?? contactReason;
    const submissionEmail = accountEmail ?? enteredEmail ?? '';

    const sharedFields = {
      name: requestName,
      requestReason: contactReason ?? '',
      requestReasonLabel: requestReasonLabel ?? '',
      email: submissionEmail,
      details: requestDetails ?? '',
    };

    const zendeskPostPromise = isCoachForm
      ? postCoachZendeskRequest({
          ...sharedFields,
        })
      : postZendeskRequest({
          ...sharedFields,
          userUuid: requester?.uuid,
          studentFullName: studentName ?? '',
        });

    const errorMessage =
      'Oops! Looks like something went wrong - please email us at coaching@remind101.com to submit your help request.';
    zendeskPostPromise
      .then(({ success }) => {
        setSubmitting(false);

        if (!success) {
          ToastActions.showErrorToast(errorMessage);
          return;
        }

        props.onSubmitSuccess(submissionEmail);
      })
      .catch(() => {
        setSubmitting(false);

        ToastActions.showErrorToast(errorMessage);
      });
  };

  const emailProps = {
    name: 'contactEmail',
    value: accountEmail,
    disabled: accountEmail,
    placeholder: 'hello@youremail.com',
  };

  const studentNameSharedProps = {
    name: 'studentName',
    disabled: studentNames.length === 1,
    placeholder: 'Sarah Doe',
  };

  const studentNameProps = hasStudents
    ? {
        value: studentName,
        options: studentNames,
        searchable: false,
        ...studentNameSharedProps,
      }
    : studentNameSharedProps;

  return (
    <React.Fragment>
      <FormElement
        label="Choose the option that matches your request"
        element={Select}
        onValidChange={value => {
          setContactReason(value);
        }}
        elementProps={{
          searchable: false,
          name: 'contactReason',
          value: contactReason,
          options: isCoachForm
            ? CoachingConstants.COACH_HELP_CONTACT_REASONS
            : CoachingConstants.HELP_CONTACT_REASONS,
        }}
      />
      <FormElement
        label="Your email (we’ll reach you here)"
        element={Input}
        onValidChange={accountEmail ? undefined : e => setEnteredEmail(e.target.value)}
        onInvalidChange={accountEmail ? undefined : () => setEnteredEmail(undefined)}
        validate={e => {
          assertions.notEmpty(e.target.value);
          assertions.email(e.target.value);
        }}
        elementProps={emailProps}
      />
      {!requester && (
        <FormElement
          label="Your name"
          element={Input}
          onValidChange={e => setRequesterName(e.target.value)}
          onInvalidChange={() => setRequesterName(undefined)}
          validate={e => {
            assertions.notEmpty(e.target.value);
            assertions.firstAndLastName(e.target.value);
          }}
          elementProps={{
            name: 'requesterName',
            placeholder: 'Jane Doe',
          }}
        />
      )}
      {!isCoachForm && (
        <FormElement
          label="Student’s first and last name"
          element={hasStudents ? Select : Input}
          onValidChange={
            hasStudents ? value => setStudentName(value) : e => setStudentName(e.target.value)
          }
          onInvalidChange={hasStudents ? undefined : () => setStudentName(undefined)}
          validate={
            hasStudents
              ? undefined
              : e => {
                  assertions.notEmpty(e.target.value);
                  assertions.firstAndLastName(e.target.value);
                }
          }
          elementProps={studentNameProps}
        />
      )}
      <FormElement
        label="Details of your request"
        element={Textarea}
        validate={e => {
          assertions.notEmpty(e.target.value);
        }}
        onValidChange={e => setRequestDetails(e.target.value)}
        onInvalidChange={() => setRequestDetails(undefined)}
        elementProps={{
          value: requestDetails,
          name: 'requestDetails',
          placeholder: 'Enter details',
        }}
      />
      <Button
        full
        size="small"
        color="koolaid"
        type="submit"
        onClick={handleSubmit}
        disabled={isSubmitDisabled()}
        loading={submitting}>
        Submit
      </Button>
    </React.Fragment>
  );
}
